import { createQuery } from 'react-query-kit';

import { buildAgentName, convertJsonPathToFieldId, getEnrichmentSuggestion } from 'hooks/useGruAgent/helpers';
import { ActionResult, SuccessfulEnrichment } from 'hooks/useGruAgent/types';
import { getWorkflow, listWorkflows } from 'services/gru/gruApi';
import { logError } from 'services/logging/logging';

type SuggestionVariables = {
  messageId?: string;
  partnerId?: string;
  isAdmin?: boolean;
  showAgentsTab?: boolean;
};

export type SuggestionPathsData = {
  paths: Set<string>;
  enrichment: SuccessfulEnrichment;
} | null;

const SUGGESTION_QUERY_KEY = 'gru-suggestion-field-paths';

export const useGruSuggestionPaths = createQuery<SuggestionPathsData, SuggestionVariables, Error>({
  primaryKey: SUGGESTION_QUERY_KEY,
  queryFn: async ({ queryKey: [, { messageId, partnerId, isAdmin }] }) => {
    const agentName = buildAgentName(messageId!, partnerId!, isAdmin ?? false);

    try {
      const workflows = await listWorkflows(agentName);
      if (workflows.length === 0) {
        return null;
      }

      const latestWorkflow = workflows[workflows.length - 1];
      if (latestWorkflow.applied_at !== null) {
        return null;
      }

      const detail = await getWorkflow(agentName, latestWorkflow.id);

      const actionResults = detail.actionResults.map((ar) => ({
        ...(ar.payload as ActionResult),
        id: ar.id,
      }));

      const enrichment = getEnrichmentSuggestion(actionResults);
      if (!enrichment) {
        return null;
      }

      const paths = new Set<string>();
      for (const item of enrichment.payload.enriched) {
        paths.add(convertJsonPathToFieldId(item.jsonpath));
      }

      return paths.size > 0 ? { paths, enrichment } : null;
    } catch (error) {
      logError(error);
      return null;
    }
  },
  enabled: (_, { messageId, partnerId, showAgentsTab }) => Boolean(messageId && partnerId && showAgentsTab),
  refetchInterval: (data) => (data ? false : 5000),
});
